// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { BenchmarkSelectorWrapper } from '@advisor-app/components';
import { Alert, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { type PortfolioLoaded } from '@shared/data-access/interfaces/common/portfolio-loaded';
import { type PortfolioPositionsCurrent } from '@shared/data-access/interfaces/redux/portfolio-state';
import { useSelectUniverse } from '@advisor-app/hooks/use-select-universe';
import {
  type TypeWorkflow,
  type WorkflowAsset,
} from '@shared/data-access/interfaces/redux/workflow-state';
import { IconButton, SectionContainer } from '@shared/ui-kit/components';
import { classNames } from '@shared/ui-kit/utils';
import { type Dispatch, type FC, type SetStateAction } from 'react';
import { SelectedAssetsSelector } from './selected-assets-selector';
import { TablePositions } from './table-positions';
import { UploaderExcel } from './uploader-excel';

export interface PortfolioPositionsProps {
  workflowType: TypeWorkflow;
  filteredDataTable: PortfolioLoaded[];
  positionsTableData: PortfolioLoaded[];
  invalidTickers: string[];
  isSomeInvalidTicker: boolean;
  handleDeleteAllPositions: VoidFunction;
  handleDeleteInvalidPositions?: VoidFunction;
  updateMyData: (rowIndex: number, columnId: string, value: any) => void;
  typeAsset: WorkflowAsset;
  portfolio: PortfolioPositionsCurrent[];
  setPositionTableData: Dispatch<SetStateAction<PortfolioLoaded[]>>;
  tableHeaderText: string;
  scroll?: boolean;
  portfolioBenchmark?: string;
  handleUpdateBenchmark?: (value: string) => void;
  mode: 'weight' | 'outstanding_balance';
  totalWeight: number;
}

export const PortfolioPositions: FC<PortfolioPositionsProps> = ({
  workflowType,
  filteredDataTable,
  isSomeInvalidTicker,
  positionsTableData,
  invalidTickers,
  handleDeleteAllPositions,
  handleDeleteInvalidPositions,
  updateMyData,
  typeAsset,
  portfolio,
  setPositionTableData,
  tableHeaderText,
  scroll,
  mode,
  totalWeight,
}) => {
  const { t } = useTranslation();
  const invalidTickersCount = invalidTickers?.length;
  const { benchmarkSelected, handleSelectBenchmark } =
    useSelectUniverse('ai-builder');
  const isSingular = invalidTickersCount === 1;
  const positionWord = isSingular
    ? t('glossary.position', { ns: 'glossary' })
    : t('glossary.positions', { ns: 'glossary' });

  return (
    <div>
      <div className="my-6 ml-6 flex">
        {workflowType !== 'tax-harvesting' && (
          <div>
            <BenchmarkSelectorWrapper
              value={benchmarkSelected?.ticker}
              onChange={handleSelectBenchmark}
            />
          </div>
        )}
      </div>
      <div className="relative grid grid-cols-[1fr_250px] items-start gap-4">
        <SectionContainer
          title={tableHeaderText}
          variant="h3"
          className="!overflow-visible"
          actions={
            <Tooltip
              title={t('portfolio.delete-all-positions', { ns: 'components' })}
            >
              <span>
                <IconButton
                  icon="Trashcan"
                  onClick={handleDeleteAllPositions}
                  disabled={!filteredDataTable.length}
                />
              </span>
            </Tooltip>
          }
        >
          {isSomeInvalidTicker && !!positionsTableData.length && (
            <Alert severity="error" className="relative mt-3 !bg-red-200">
              {t(
                isSingular
                  ? 'portfolio.incorrect-positions-message-singular'
                  : 'portfolio.incorrect-positions-message-plural',
                {
                  ns: 'components',
                  invalidTickersCount,
                  positionWord,
                  portfolioLength: portfolio.length,
                },
              )}
              <button
                className="absolute right-5 top-[10px] rounded-md bg-red-300 px-3 py-1 font-bold text-red-600"
                onClick={handleDeleteInvalidPositions}
                data-testid="delete-invalid-positions-button"
              >
                {t('portfolio.remove-all', { ns: 'components' })}
              </button>
            </Alert>
          )}

          <div
            className={classNames({
              'relative mt-2 h-[420px] overflow-y-auto': scroll,
            })}
          >
            <TablePositions
              excelData={filteredDataTable}
              setExcelData={setPositionTableData}
              updateMyData={updateMyData}
              invalidPositions={invalidTickers}
              mode={mode}
              totalWeight={totalWeight}
            />
          </div>
        </SectionContainer>
        <div className="flex flex-col gap-y-4">
          <SelectedAssetsSelector onSelectAssets={setPositionTableData} />
          <UploaderExcel
            typeAsset={typeAsset}
            setExcelData={setPositionTableData}
          />
        </div>
      </div>
    </div>
  );
};
